import { CartAddEvent } from '@theme/events';

/**
 * "Birlikte Daha İyi" listesini yöneten web bileşeni.
 *
 * Ayrı bir "Sepete Ekle" butonu yok. Bunun yerine, sayfadaki ANA ürünün
 * kendi "Sepete Ekle" butonuyla sepete eklendiğini (CartAddEvent /
 * ThemeEvents.cartUpdate, source: 'product-form-component') dinler; bu olay
 * ana ürün için gerçekleştiğinde, o an işaretli olan "Birlikte Daha İyi"
 * ürünlerini de ardından /cart/add.js üzerinden toplu olarak ekler.
 *
 * Böylece müşteri kutuları işaretleyip sayfadaki tek bir "Sepete Ekle"
 * butonuna bastığında hem ana ürün hem de seçtiği ürünler birlikte sepete
 * eklenmiş olur.
 */
class BetterTogetherList extends HTMLElement {
  connectedCallback() {
    this.currentProductId = this.dataset.currentProductId;
    this.liveRegion = this.querySelector('[data-better-together-live-region]');

    this.checkboxes = Array.from(this.querySelectorAll('[data-better-together-toggle]'));

    document.addEventListener(CartAddEvent.eventName, this.handleCartAdd);
  }

  disconnectedCallback() {
    document.removeEventListener(CartAddEvent.eventName, this.handleCartAdd);
  }

  /**
   * @returns {HTMLElement[]} Şu anda işaretli satırların (better-together-item) listesi.
   */
  getSelectedItems() {
    return this.checkboxes
      .filter((checkbox) => /** @type {HTMLInputElement} */ (checkbox).checked)
      .map((checkbox) => checkbox.closest('better-together-item'))
      .filter(/** @returns {item is HTMLElement} */ (item) => item != null);
  }

  /**
   * @param {CustomEvent} event
   */
  handleCartAdd = (event) => {
    const data = event.detail?.data;
    if (!data || data.source !== 'product-form-component' || data.didError) return;
    if (String(data.productId) !== String(this.currentProductId)) return;

    this.addSelectedToCart();
  };

  async addSelectedToCart() {
    const selectedItems = this.getSelectedItems();
    if (selectedItems.length === 0) return;

    const items = selectedItems.map((item) => ({
      id: Number(item.dataset.variantId),
      quantity: 1,
    }));

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sections = Array.from(cartItemsComponents)
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter((id) => Boolean(id));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items, sections }),
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.message || result.description || 'Sepete eklenirken bir hata oluştu.');
      }

      document.dispatchEvent(
        new CartAddEvent({}, this.id || 'better-together', {
          source: 'better-together',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: result.sections,
        })
      );

      if (this.liveRegion) {
        this.liveRegion.textContent = 'Seçtiğiniz ürünler de sepete eklendi.';
      }

      selectedItems.forEach((item) => {
        const checkbox = /** @type {HTMLInputElement | null} */ (item.querySelector('[data-better-together-toggle]'));
        if (checkbox) checkbox.checked = false;
      });
    } catch (error) {
      console.error(error);
      if (this.liveRegion) {
        this.liveRegion.textContent = 'Seçtiğiniz ürünler sepete eklenirken bir hata oluştu.';
      }
    }
  }
}

if (!customElements.get('better-together-list')) {
  customElements.define('better-together-list', BetterTogetherList);
}
